import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Crown, Check, ArrowLeft, Loader2, CreditCard, ExternalLink, Infinity, Palette, Image, BookOpen, Tag, Headphones } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';

type Interval = 'month' | 'year';

export default function PremiumPage() {
  const { toast } = useToast();
  const { isPremium, monthlySeconds, remainingSeconds, refresh } = useSubscription();
  const [code, setCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [checkingOut, setCheckingOut] = useState<Interval | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<Interval>('year');
  const [openingPortal, setOpeningPortal] = useState(false);

  const handleActivate = async () => {
    if (!code.trim()) return;
    setActivating(true);
    try {
      await api.activate(code.trim());
      toast({ title: 'プレミアムプランが有効化されました!' });
      setCode('');
      refresh();
    } catch (err) {
      toast({
        title: '招待コードの認証に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setActivating(false);
    }
  };

  const handlePortal = async () => {
    setOpeningPortal(true);
    try {
      const { portalUrl } = await api.createPortalSession();
      if (!portalUrl) throw new Error('ポータルURLが取得できませんでした');
      window.location.href = portalUrl;
    } catch (err) {
      toast({
        title: 'ポータルの取得に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleCheckout = async (interval: Interval) => {
    setCheckingOut(interval);
    try {
      const { checkoutUrl } = await api.createCheckout(interval);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      toast({
        title: '決済ページの取得に失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
      setCheckingOut(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/settings">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">プラン</h1>
      </div>

      {/* Current status */}
      <Card className={isPremium ? 'border-amber-500/30 bg-amber-500/5' : ''}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {isPremium && <Crown className="h-5 w-5 text-amber-500" />}
            現在のプラン: {isPremium ? 'プレミアム' : 'フリー'}
          </CardTitle>
          <CardDescription>
            {isPremium ? (
              'すべての機能が無制限で利用できます'
            ) : (
              <>今月の録音時間: {Math.floor(monthlySeconds / 60)}分 / 10分 (残り {remainingSeconds > 0 ? Math.max(1, Math.ceil(remainingSeconds / 60)) : 0}分)</>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Premium user: management section */}
      {isPremium && (
        <>
          {/* Premium features list */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">プレミアム機能</CardTitle>
              <CardDescription>すべての機能をお楽しみください</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {([
                  { icon: Infinity, label: '録音時間無制限', color: 'text-blue-500' },
                  { icon: BookOpen, label: '辞書100件', color: 'text-emerald-500' },
                  { icon: Tag, label: 'カスタムタグ50個', color: 'text-purple-500' },
                  { icon: Palette, label: '12テーマ全開放', color: 'text-pink-500' },
                  { icon: Image, label: '画像添付', color: 'text-orange-500' },
                  { icon: Headphones, label: '優先サポート', color: 'text-sky-500' },
                ] as const).map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex items-center gap-2.5 rounded-lg bg-muted/30 px-3 py-2.5">
                    <Icon className={`h-4 w-4 shrink-0 ${color}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Manage subscription */}
          <Button
            size="lg"
            variant="outline"
            className="w-full gap-2"
            onClick={handlePortal}
            disabled={openingPortal}
          >
            {openingPortal ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ExternalLink className="h-5 w-5" />
            )}
            サブスクリプションを管理
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            支払い方法の変更・プランの解約・請求履歴の確認ができます
          </p>
        </>
      )}

      {/* Plan comparison — free users */}
      {!isPremium && (
        <>
          {/* Comparison matrix */}
          <Card>
            <CardContent className="pt-5 px-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left font-medium text-muted-foreground px-4 pb-3"></th>
                    <th className="text-center font-semibold px-2 pb-3">フリー</th>
                    <th className="text-center font-semibold px-2 pb-3 text-amber-600 dark:text-amber-400">
                      <span className="flex items-center justify-center gap-1">
                        <Crown className="h-3.5 w-3.5" />
                        プレミアム
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    ['録音時間', '月10分', '無制限'],
                    ['辞書登録', '5件', '100件'],
                    ['カスタムタグ', '5個', '50個'],
                    ['テーマ', '1種類', '12種類'],
                    ['画像添付', false, true],
                    ['振り返り', '月1回', '月10回'],
                    ['振り返り期間', '1ヶ月', '全期間'],
                    ['優先サポート', false, true],
                  ] as [string, string | boolean, string | boolean][]).map(([feature, free, premium], i) => (
                    <tr key={feature} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                      <td className="px-4 py-2.5 font-medium">{feature}</td>
                      <td className="text-center px-2 py-2.5">
                        {typeof free === 'boolean'
                          ? (free ? <Check className="h-4 w-4 mx-auto text-green-500" /> : <span className="text-muted-foreground">—</span>)
                          : <span className="text-muted-foreground">{free}</span>
                        }
                      </td>
                      <td className="text-center px-2 py-2.5">
                        {typeof premium === 'boolean'
                          ? (premium ? <Check className="h-4 w-4 mx-auto text-amber-500" /> : <span className="text-muted-foreground">—</span>)
                          : <span className="font-medium text-amber-600 dark:text-amber-400">{premium}</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Interval selector */}
          <div className="flex gap-3">
            <button
              onClick={() => setSelectedInterval('month')}
              className={`flex-1 rounded-xl border-2 px-3 py-3 text-center transition-colors ${
                selectedInterval === 'month'
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-border hover:bg-accent'
              }`}
            >
              <p className="text-xl font-bold">¥780</p>
              <p className="text-xs text-muted-foreground">月額</p>
            </button>
            <button
              onClick={() => setSelectedInterval('year')}
              className={`flex-1 rounded-xl border-2 px-3 py-3 text-center transition-colors ${
                selectedInterval === 'year'
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-border hover:bg-accent'
              }`}
            >
              <p className="text-xl font-bold">¥6,500</p>
              <p className="text-xs text-muted-foreground">年額 (30%OFF)</p>
            </button>
          </div>

          {/* Checkout button */}
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={() => handleCheckout(selectedInterval)}
            disabled={checkingOut !== null}
          >
            {checkingOut ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <CreditCard className="h-5 w-5" />
            )}
            {selectedInterval === 'year' ? '7日間無料で試す' : 'プレミアムを始める'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            {selectedInterval === 'year'
              ? '無料期間終了後、年額 ¥6,500が請求されます。いつでもキャンセル可能です。'
              : '月額 ¥780が請求されます。いつでもキャンセル可能です。'}
          </p>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">または</span>
            </div>
          </div>

          {/* Activation code */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">招待コード</CardTitle>
              <CardDescription>お持ちのコードでプレミアムプランを有効化できます</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="コードを入力"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleActivate()}
                />
                <Button onClick={handleActivate} disabled={activating || !code.trim()}>
                  {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : '有効化'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
